import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swiper from 'swiper';
import { Autoplay, Navigation, EffectFade } from 'swiper/modules';

declare global {
  interface Window {
    WOW: any;
    Swiper: any;
    $: any;
    jQuery: any;
  }
}

interface Update {
  id: number;
  title: string;
  category: string;
  image: string;
  excerpt: string;
  date: string;
  author: string;
}

interface Volunteer {
  id: number;
  name: string;
  role: string;
  image: string;
  bio: string;
  order_position: number;
  active: boolean;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image: string;
  order_position: number;
  active: boolean;
}

const Home: React.FC = () => {
  const [featuredUpdates, setFeaturedUpdates] = useState<Update[]>([]);
  const [recentBlogUpdates, setRecentBlogUpdates] = useState<Update[]>([]);
  const [coreTeamMembers, setCoreTeamMembers] = useState<TeamMember[]>([]);
  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);

  useEffect(() => {
    // Fetch featured updates
    fetch('http://localhost:5000/api/updates?featured=true&limit=2')
      .then(res => res.json())
      .then(data => setFeaturedUpdates(data))
      .catch(err => console.error('Error fetching updates:', err));

    // Fetch recent blog updates (3 most recent)
    fetch('http://localhost:5000/api/updates?limit=3')
      .then(res => res.json())
      .then(data => setRecentBlogUpdates(data))
      .catch(err => console.error('Error fetching blog updates:', err));

    // Fetch volunteers
    fetch('http://localhost:5000/api/volunteers?active=true')
      .then(res => res.json())
      .then(data => setVolunteers(data.slice(0, 3)))
      .catch(err => console.error('Error fetching volunteers:', err));

    // Fetch top 3 core team members (order_position < 1000)
    fetch('http://localhost:5000/api/team?active=true')
      .then(res => res.json())
      .then((data: TeamMember[]) => {
        const topCore = (data || [])
          .filter((member) => Number(member.order_position || 0) < 1000)
          .sort((a, b) => Number(a.order_position || 0) - Number(b.order_position || 0))
          .slice(0, 3);
        setCoreTeamMembers(topCore);
      })
      .catch(err => console.error('Error fetching core team members:', err));
  }, []);

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }

    // Initialize Swiper sliders
    const SwiperConstructor = window.Swiper || Swiper;
    const swiperInstances: any[] = [];

    const initSwiper = (selector: string, options: any) => {
      if (!document.querySelector(selector)) return;
      const instance = new SwiperConstructor(selector, options);
      if (instance) swiperInstances.push(instance);
    };

    if (SwiperConstructor) {
      // Banner Slider with fade effect
      initSwiper('.banner-two__slider', {
        modules: [Autoplay, Navigation, EffectFade],
        loop: true,
        speed: 1500,
        effect: 'fade',
        fadeEffect: {
          crossFade: true
        },
        autoplay: {
          delay: 5000,
          disableOnInteraction: false,
        },
        navigation: {
          nextEl: '.banner-two__arry-next',
          prevEl: '.banner-two__arry-prev',
        },
      });

      // Cause Slider
      initSwiper('.cause__slider', {
        modules: [Autoplay, Navigation],
        loop: true,
        spaceBetween: 30,
        speed: 1000,
        autoplay: {
          delay: 4000,
          disableOnInteraction: false,
        },
        navigation: {
          nextEl: '.cause__arry-next',
          prevEl: '.cause__arry-prev',
        },
        breakpoints: {
          0: {
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 2,
          },
          992: {
            slidesPerView: 3,
          },
          1400: {
            slidesPerView: 4,
          },
        },
      });

      // Brand Slider
      initSwiper('.brand__slider', {
        modules: [Autoplay],
        loop: true,
        spaceBetween: 30,
        speed: 1000,
        autoplay: {
          delay: 3000,
          disableOnInteraction: false,
        },
        breakpoints: {
          0: {
            slidesPerView: 1,
          },
          576: {
            slidesPerView: 2,
          },
          768: {
            slidesPerView: 3,
          },
          992: {
            slidesPerView: 4,
          },
          1200: {
            slidesPerView: 4,
          },
        },
      });
    }

    // Initialize Counter Up
    if (window.$ && window.$.fn.counterUp) {
      window.$('.count').counterUp({
        delay: 10,
        time: 1000
      });
    }

    // Initialize Magnific Popup for video
    if (window.$ && window.$.fn.magnificPopup) {
      window.$('.video-popup').magnificPopup({
        type: 'iframe',
        iframe: {
          markup: '<div class="mfp-iframe-scaler">' +
            '<div class="mfp-close"></div>' +
            '<iframe class="mfp-iframe" frameborder="0" allowfullscreen></iframe>' +
            '</div>',
          patterns: {
            youtube: {
              index: 'youtube.com/',
              id: 'v=',
              src: 'https://www.youtube.com/embed/%id%?autoplay=1'
            }
          },
          srcAction: 'iframe_src',
        }
      });
    }

    // Cleanup function
    return () => {
      swiperInstances.forEach((instance) => {
        if (instance && typeof instance.destroy === 'function') {
          instance.destroy(true, true);
        }
      });
    };
  }, [featuredUpdates, volunteers, coreTeamMembers]);

  return (
    <>
      {/* Banner Section */}
      <section className="banner-two-area">
        <div className="swiper banner-two__slider">
          <div className="swiper-wrapper">
            <div className="swiper-slide">
              <div className="slide-bg" data-background="/assets/images/banner/banner-image1.png"></div>
              <div className="container">
                <div className="banner-two__content">
                  <h4 data-animation="fadeInUp" data-delay=".3s">Welcome To New Life Nepal</h4>
                  <h1 data-animation="fadeInUp" data-delay=".5s">Together We Can Make<br /> The Change</h1>
                  <div className="btn-one mt-50" data-animation="fadeInUp" data-delay="1s">
                    <span className="btn-circle"></span>
                    <Link to="/contact" className="btn-inner">
                      <span className="btn-text">DONATE NOW</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="swiper-slide">
              <div className="slide-bg" data-background="/assets/images/banner/banner-image2.jpg"></div>
              <div className="container">
                <div className="banner-two__content">
                  <h4 data-animation="fadeInUp" data-delay=".3s">Change The World Together</h4>
                  <h1 data-animation="fadeInUp" data-delay=".5s">Support our kids  <br /> Love them as you can</h1>
                  <div className="btn-one mt-50" data-animation="fadeInUp" data-delay="1s">
                    <span className="btn-circle"></span>
                    <Link to="/contact" className="btn-inner">
                      <span className="btn-text">DONATE NOW</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            <div className="swiper-slide">
              <div className="slide-bg" data-background="/assets/images/banner/hero3.png"></div>
              <div className="container">
                <div className="banner-two__content">
                  <h4 data-animation="fadeInUp" data-delay=".3s">Make A Difference Today</h4>
                  <h1 data-animation="fadeInUp" data-delay=".5s">Help Us Build A<br /> Better Tomorrow</h1>
                  <div className="btn-one mt-50" data-animation="fadeInUp" data-delay="1s">
                    <span className="btn-circle"></span>
                    <Link to="/contact" className="btn-inner">
                      <span className="btn-text">DONATE NOW</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="banner-two__arry-btn">
          <button className="arry-prev mb-15 banner-two__arry-prev active">
            <i className="fa-light fa-chevron-left"></i>
          </button>
          <button className="arry-next banner-two__arry-next">
            <i className="fa-light fa-chevron-right"></i>
          </button>
        </div>
        <ul className="banner-two__social">
          <li><a href="#0"><i className="fa-brands fa-facebook-f"></i></a></li>
          <li><a href="#0" className="active"><i className="fa-brands fa-twitter"></i></a></li>
          <li><a href="#0"><i className="fa-brands fa-linkedin-in"></i></a></li>
        </ul>
        <div className="banner-two__shape wow slideInLeft" data-wow-delay="600ms" data-wow-duration="1500ms">
          <img className="banner__line__animation" src="/assets/images/shape/banner-line.png" alt="shape" />
        </div>
      </section>

      {/* Service Area */}
      <section className="service-area pt-100 pb-100 sub-bg">
        <div className="container">
          <div className="row g-4">
            <div className="col-xl-4 col-md-6 wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
              <div className="service__item">
                <div className="service__icon"><img src="/assets/images/icon/service1.png" alt="icon" /></div>
                <div className="service__content">
                  <h3 className="text-white">Kids Education</h3>
                  <p className="text-white">Every is to be welcomed in certain circumstances</p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6 wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
              <div className="service__item active">
                <div className="service__icon"><img src="/assets/images/icon/service2.png" alt="icon" /></div>
                <div className="service__content">
                  <h3 className="text-white">Healthy Food</h3>
                  <p className="text-white">Every is to be welcomed in certain circumstances</p>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-md-6 wow fadeInUp" data-wow-delay="400ms" data-wow-duration="1500ms">
              <div className="service__item">
                <div className="service__icon"><img src="/assets/images/icon/service3.png" alt="icon" /></div>
                <div className="service__content">
                  <h3 className="text-white">Medical Help</h3>
                  <p className="text-white">Every is to be welcomed in certain circumstances</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Area */}
      <section className="about-area pt-120 pb-120">
        <div className="container">
          <div className="row g-2 align-items-center">
            <div className="col-lg-6 wow fadeInLeft" data-wow-delay="200ms" data-wow-duration="1500ms">
              <div className="about__image">
                <div className="dots about-dot__animation">
                  <img src="/assets/images/shape/about-dot.png" alt="dots shape" />
                </div>
                <div className="sm-image">
                  <img src="/assets/images/about/about-image2.png" alt="about secondary" />
                </div>
                <div className="image">
                  <img src="/assets/images/about/about-image1.png" alt="about primary" />
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="section-header mb-20">
                <h5 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
              
                </h5>
                <h2 className="wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
                 New Life Nepal is an organization committed to improving lives and fostering hope in communities in need.
                </h2>
                <p className="wow fadeInUp" data-wow-delay="400ms" data-wow-duration="1500ms">
                  It is a long established fact that a reader will be distracted the readable content of a
                  page when looking at layout the point of using lorem Ipsum <br />
                  less normal distribution of letters.
                </p>
              </div>
              <div className="about__content">
                <div className="progress-area wow fadeInDown" data-wow-delay="00ms" data-wow-duration="1500ms">
                 
                 
                </div>
                <div className="row g-4 mt-10 wow fadeInDown" data-wow-delay="200ms" data-wow-duration="1500ms">
                  <div className="col-6">
                    <h6 className="fs-14">
                      <svg className="me-1" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_20_5)">
                          <path d="M17.4853 2.46073C17.2205 2.19547 16.7909 2.19502 16.5261 2.4596L8.38765 10.5765L5.45133 7.38742C5.1976 7.112 4.76863 7.09415 4.49276 7.34783C4.2171 7.60156 4.19945 8.03074 4.45317 8.3064L7.86776 12.0147C7.92961 12.082 8.00443 12.136 8.08771 12.1736C8.17099 12.2111 8.26101 12.2315 8.35235 12.2334C8.35731 12.2336 8.36209 12.2336 8.36683 12.2336C8.54636 12.2335 8.71855 12.1624 8.84577 12.0357L17.484 3.42018C17.7494 3.15563 17.7499 2.72598 17.4853 2.46073Z" fill="#03A84A" />
                          <path d="M17.3216 8.32159C16.9469 8.32159 16.6432 8.62527 16.6432 9C16.6432 13.2146 13.2146 16.6432 9 16.6432C4.78561 16.6432 1.35679 13.2146 1.35679 9C1.35679 4.78561 4.78561 1.35679 9 1.35679C9.3747 1.35679 9.67841 1.05311 9.67841 0.67841C9.67841 0.30368 9.3747 0 9 0C4.03734 0 0 4.03734 0 9C0 13.9624 4.03734 18 9 18C13.9624 18 18 13.9624 18 9C18 8.6253 17.6963 8.32159 17.3216 8.32159Z" fill="#03A84A" />
                        </g>
                        <defs><clipPath><rect width="18" height="18" fill="white" /></clipPath></defs>
                      </svg>
                      Immediate Assistance
                    </h6>
                  </div>
                  <div className="col-6">
                    <h6 className="fs-14">
                      <svg className="me-1" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_20_5)">
                          <path d="M17.4853 2.46073C17.2205 2.19547 16.7909 2.19502 16.5261 2.4596L8.38765 10.5765L5.45133 7.38742C5.1976 7.112 4.76863 7.09415 4.49276 7.34783C4.2171 7.60156 4.19945 8.03074 4.45317 8.3064L7.86776 12.0147C7.92961 12.082 8.00443 12.136 8.08771 12.1736C8.17099 12.2111 8.26101 12.2315 8.35235 12.2334C8.35731 12.2336 8.36209 12.2336 8.36683 12.2336C8.54636 12.2335 8.71855 12.1624 8.84577 12.0357L17.484 3.42018C17.7494 3.15563 17.7499 2.72598 17.4853 2.46073Z" fill="#03A84A" />
                          <path d="M17.3216 8.32159C16.9469 8.32159 16.6432 8.62527 16.6432 9C16.6432 13.2146 13.2146 16.6432 9 16.6432C4.78561 16.6432 1.35679 13.2146 1.35679 9C1.35679 4.78561 4.78561 1.35679 9 1.35679C9.3747 1.35679 9.67841 1.05311 9.67841 0.67841C9.67841 0.30368 9.3747 0 9 0C4.03734 0 0 4.03734 0 9C0 13.9624 4.03734 18 9 18C13.9624 18 18 13.9624 18 9C18 8.6253 17.6963 8.32159 17.3216 8.32159Z" fill="#03A84A" />
                        </g>
                        <defs><clipPath><rect width="18" height="18" fill="white" /></clipPath></defs>
                      </svg>
                      Quick Fundraise
                    </h6>
                  </div>
                  <div className="col-6">
                    <h6 className="fs-14">
                      <svg className="me-1" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_20_5)">
                          <path d="M17.4853 2.46073C17.2205 2.19547 16.7909 2.19502 16.5261 2.4596L8.38765 10.5765L5.45133 7.38742C5.1976 7.112 4.76863 7.09415 4.49276 7.34783C4.2171 7.60156 4.19945 8.03074 4.45317 8.3064L7.86776 12.0147C7.92961 12.082 8.00443 12.136 8.08771 12.1736C8.17099 12.2111 8.26101 12.2315 8.35235 12.2334C8.35731 12.2336 8.36209 12.2336 8.36683 12.2336C8.54636 12.2335 8.71855 12.1624 8.84577 12.0357L17.484 3.42018C17.7494 3.15563 17.7499 2.72598 17.4853 2.46073Z" fill="#03A84A" />
                          <path d="M17.3216 8.32159C16.9469 8.32159 16.6432 8.62527 16.6432 9C16.6432 13.2146 13.2146 16.6432 9 16.6432C4.78561 16.6432 1.35679 13.2146 1.35679 9C1.35679 4.78561 4.78561 1.35679 9 1.35679C9.3747 1.35679 9.67841 1.05311 9.67841 0.67841C9.67841 0.30368 9.3747 0 9 0C4.03734 0 0 4.03734 0 9C0 13.9624 4.03734 18 9 18C13.9624 18 18 13.9624 18 9C18 8.6253 17.6963 8.32159 17.3216 8.32159Z" fill="#03A84A" />
                        </g>
                        <defs><clipPath><rect width="18" height="18" fill="white" /></clipPath></defs>
                      </svg>
                      Long-Time Support
                    </h6>
                  </div>
                  <div className="col-6">
                    <h6 className="fs-14">
                      <svg className="me-1" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <g clipPath="url(#clip0_20_5)">
                          <path d="M17.4853 2.46073C17.2205 2.19547 16.7909 2.19502 16.5261 2.4596L8.38765 10.5765L5.45133 7.38742C5.1976 7.112 4.76863 7.09415 4.49276 7.34783C4.2171 7.60156 4.19945 8.03074 4.45317 8.3064L7.86776 12.0147C7.92961 12.082 8.00443 12.136 8.08771 12.1736C8.17099 12.2111 8.26101 12.2315 8.35235 12.2334C8.35731 12.2336 8.36209 12.2336 8.36683 12.2336C8.54636 12.2335 8.71855 12.1624 8.84577 12.0357L17.484 3.42018C17.7494 3.15563 17.7499 2.72598 17.4853 2.46073Z" fill="#03A84A" />
                          <path d="M17.3216 8.32159C16.9469 8.32159 16.6432 8.62527 16.6432 9C16.6432 13.2146 13.2146 16.6432 9 16.6432C4.78561 16.6432 1.35679 13.2146 1.35679 9C1.35679 4.78561 4.78561 1.35679 9 1.35679C9.3747 1.35679 9.67841 1.05311 9.67841 0.67841C9.67841 0.30368 9.3747 0 9 0C4.03734 0 0 4.03734 0 9C0 13.9624 4.03734 18 9 18C13.9624 18 18 13.9624 18 9C18 8.6253 17.6963 8.32159 17.3216 8.32159Z" fill="#03A84A" />
                        </g>
                        <defs><clipPath><rect width="18" height="18" fill="white" /></clipPath></defs>
                      </svg>
                      Handle With Care
                    </h6>
                  </div>
                </div>
                <div className="d-flex align-content-center flex-wrap gap-4 mt-40 wow fadeInDown" data-wow-delay="400ms" data-wow-duration="1500ms">
                  <div className="btn-two">
                    <span className="btn-circle"></span>
                    <Link to="/about" className="btn-inner">
                      <span className="btn-text">EXPLORE MORE</span>
                    </Link>
                  </div>
                  <div className="about-info">
                    <img src="/assets/images/about/about-info.png" alt="Bishal Dahal portrait" />
                    <div className="con">
                      <h5 className="fs-16">Bishal Dahal</h5>
                      <span className="sm-font">Secretary, New Life Nepal</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Video Area */}
      <section className="video-area">
        <div className="row g-0">
          <div className="col-md-5">
            <div className="video__left-item bg-image h-100" data-background="/assets/images/video/video-left-image.jpg">
              <div className="video__btn-wrp">
                <div className="video-btn video-pulse">
                  <a href="https://www.youtube.com/watch?v=KYf_XKueY7s" target="_blank" rel="noopener noreferrer">
                    <i className="fa-solid fa-play"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-7">
            <div className="video__right-item bg-image pt-120 pb-120 pl-80" data-background="/assets/images/video/video-right-image.jpg">
              <div className="video__content">
                <div className="section-header mb-40">
                  <h5 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
                    <i className="fa-regular fa-angles-left pe-1"></i> Watch Videos <i className="fa-regular fa-angles-right ps-1"></i>
                  </h5>
                  <h2 className="text-white wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
                    Enhance the Depth of Meaning in Your Life and Family
                  </h2>
                  <p className="text-white wow fadeInUp" data-wow-delay="400ms" data-wow-duration="1500ms">
                    It is a long established fact that a reader will be distracted the readable <br /> content of a page when looking at layout the
                    point of using lorem Ipsum <br />
                    less normal distribution of letters.
                  </p>
                </div>
                <div className="d-flex align-items-center justify-content-between gap-4 flex-wrap">
                  <div className="info wow fadeInDown" data-wow-delay="00ms" data-wow-duration="1500ms">
                    <h2 className="text-white"><span className="count text-white">50</span>%</h2>
                    <p className="mt-10 text-white">kids Need Help</p>
                  </div>
                  <div className="info wow fadeInDown" data-wow-delay="200ms" data-wow-duration="1500ms">
                    <h2 className="text-white">$<span className="count text-white">500</span>k</h2>
                    <p className="mt-10 text-white">Our Goal</p>
                  </div>
                  <div className="info wow fadeInDown" data-wow-delay="400ms" data-wow-duration="1500ms">
                    <h2 className="text-white">$<span className="count text-white">125</span>k</h2>
                    <p className="mt-10 text-white">Raised Now</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Team Area */}
      <section className="team-area pt-120 pb-120 sub-bg">
        <div className="container">
          <div className="section-header mb-60 text-center">
            <h5 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
              <i className="fa-regular fa-angles-left pe-1"></i> Core Team Members <i className="fa-regular fa-angles-right ps-1"></i>
            </h5>
            <h2 className="wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">Leadership Team</h2>
          </div>
          <div className="row g-4">
            {coreTeamMembers.map((member, index) => (
              <div key={member.id} className="col-lg-4 col-sm-6 wow fadeInUp" data-wow-delay={`${index * 200}ms`} data-wow-duration="1500ms">
                <div className="team__item image">
                  <img src={member.image || '/assets/images/team/team-image1.jpg'} alt={member.name} />
                  <div className="team__content">
                    <div className="content">
                      <h4><Link to="/about/team" className="primary-hover">{member.name}</Link></h4>
                      <span>{member.role}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {coreTeamMembers.length === 0 && (
              <div className="col-12 text-center">
                <p>Loading team members...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Team Area */}
      <section className="team-area pt-120 pb-120 sub-bg">
        <div className="container">
          <div className="section-header mb-60 text-center">
            <h5 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
              <i className="fa-regular fa-angles-left pe-1"></i> Our Volunteers <i className="fa-regular fa-angles-right ps-1"></i>
            </h5>
            <h2 className="wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">Volunteers Our Precious Assets</h2>
          </div>
          <div className="row g-4">
            {volunteers.map((volunteer, index) => (
              <div key={volunteer.id} className="col-lg-4 col-sm-6 wow fadeInUp" data-wow-delay={`${index * 200}ms`} data-wow-duration="1500ms">
                <div className="team__item image">
                  <img src={volunteer.image || '/assets/images/volunteers/team-image1.jpg'} alt={volunteer.name} />
                  <div className="team__content">
                    <div className="content">
                      <h4><Link to="/volunteers" className="primary-hover">{volunteer.name}</Link></h4>
                      <span>{volunteer.role}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {volunteers.length === 0 && (
              <div className="col-12 text-center">
                <p>Loading volunteers...</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Blog Area */}
      <section className="blog-area pt-120">
        <div className="container">
          <div className="section-header mb-60 text-center">
            <h5 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">
              <i className="fa-regular fa-angles-left pe-1"></i> Blog & news <i className="fa-regular fa-angles-right ps-1"></i>
            </h5>
            <h2 className="wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">Latest News Update</h2>
          </div>
          <div className="row g-4">
            {recentBlogUpdates.map((blog, index) => (
              <div key={blog.id} className="col-xl-4 col-md-6 wow fadeInUp" data-wow-delay={`${index * 200}ms`} data-wow-duration="1500ms">
                <div className="blog__item">
                  <div className="image">
                    <img 
                      src={blog.image || '/assets/images/cause/cause-image1.jpg'} 
                      alt={blog.title} 
                      style={{ width: '100%', height: '224px', objectFit: 'cover' }}
                    />
                    <span className="blog-tag">{blog.category}</span>
                  </div>
                  <div className="blog__content pt-4">
                    <ul>
                      <li>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M14.5435 5.19275C14.5435 7.69093 12.4989 9.7355 10.0008 9.7355C7.50262 9.7355 5.45804 7.69093 5.45804 5.19275C5.45804 2.69457 7.50258 0.65 10.0008 0.65C12.4989 0.65 14.5435 2.69458 14.5435 5.19275Z" stroke="#03A84A" strokeWidth="1.3" />
                          <path d="M18.2644 14.6706C18.1052 14.9458 17.9241 15.2073 17.7169 15.4766L17.7168 15.4765L17.7089 15.4873C17.4203 15.8788 17.0845 16.2373 16.7294 16.5924C16.4326 16.8892 16.0932 17.186 15.7567 17.4385C14.0794 18.6911 12.0621 19.3499 9.97814 19.3499C7.89836 19.3499 5.88506 18.6938 4.20976 17.4461C3.84588 17.1504 3.51367 16.8792 3.22686 16.5924L3.2199 16.5854L3.21272 16.5787C2.85663 16.2436 2.54238 15.8877 2.24745 15.4874L2.24747 15.4873L2.24414 15.4829C2.06192 15.24 1.8732 14.9756 1.71919 14.7169C1.83618 14.4559 1.98455 14.1847 2.14521 13.9526L2.14533 13.9527L2.15284 13.9413C3.06984 12.5556 4.53705 11.6388 6.16642 11.4148L6.186 11.4121L6.20538 11.4082C6.23087 11.4031 6.29494 11.4117 6.34548 11.4496L6.34546 11.4496L6.34947 11.4525C7.41651 12.2401 8.68629 12.6453 10.0008 12.6453C11.3153 12.6453 12.5851 12.2401 13.6521 11.4525L13.6521 11.4525L13.6561 11.4496C13.6715 11.438 13.7403 11.408 13.8492 11.4167C15.4688 11.6435 16.9121 12.5568 17.8524 13.9468L17.8524 13.9469L17.8564 13.9526C18.0165 14.1839 18.1557 14.4231 18.2644 14.6706Z" stroke="#03A84A" strokeWidth="1.3" />
                        </svg>
                        <Link to={`/updates/${blog.id}`}><span className="primary-hover transition">By {blog.author}</span></Link>
                      </li>
                      <li>
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6.66667 4.7915C6.325 4.7915 6.04167 4.50817 6.04167 4.1665V1.6665C6.04167 1.32484 6.325 1.0415 6.66667 1.0415C7.00833 1.0415 7.29167 1.32484 7.29167 1.6665V4.1665C7.29167 4.50817 7.00833 4.7915 6.66667 4.7915ZM13.3333 4.7915C12.9917 4.7915 12.7083 4.50817 12.7083 4.1665V1.6665C12.7083 1.32484 12.9917 1.0415 13.3333 1.0415C13.675 1.0415 13.9583 1.32484 13.9583 1.6665V4.1665C13.9583 4.50817 13.675 4.7915 13.3333 4.7915Z" fill="#03A84A" />
                          <path d="M13.3333 18.9582H6.66667C3.625 18.9582 1.875 17.2082 1.875 14.1665V7.08317C1.875 4.0415 3.625 2.2915 6.66667 2.2915H13.3333C16.375 2.2915 18.125 4.0415 18.125 7.08317V14.1665C18.125 17.2082 16.375 18.9582 13.3333 18.9582Z" fill="#03A84A" />
                        </svg>
                        <span>{new Date(blog.date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </li>
                    </ul>
                    <h4 className="mt-20 pb-25 bor-bottom">
                      <Link to={`/updates/${blog.id}`} className="primary-hover">{blog.title}</Link>
                    </h4>
                    <Link className="mt-4" to={`/updates/${blog.id}`}>
                      <span className="read-more fw-bold transition">Read More <i className="fa-solid fa-arrow-right ms-1"></i></span>
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Brand Area */}
      <div className="brand-area pt-100 pb-100 wow fadeInDown" data-wow-delay="200ms" data-wow-duration="1500ms">
        <div className="container">
          <div className="swiper brand__slider">
            <div className="swiper-wrapper">
              {[1, 2, 3, 4, 5, 6, 1, 2, 3, 4, 5, 6].map((item, index) => (
                <div key={index} className="swiper-slide">
                  <div className="image text-center">
                    <img src={`/assets/images/brand/brand-image${item}.png`} alt="brand" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
